import { motion } from "framer-motion";
import { I18n } from "@assets/strings";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useProfileRole } from "@domain/currentProfile/hooks";
import { OrganizationType } from "@domain/organization/organizationType";
import { NextButton } from "@ui/components/buttons/nextButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { row, stack } from "@ui/components/layout";
import { OrganizationEditor } from "@ui/components/organization/organizationEditor";
import { Page } from "@ui/components/page";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { desktop, gap, mobile } from "@ui/styles/utils";

export const NewOrganizationPage: React.FC = () => {
	const [selectedType, setSelectedType] = useState<OrganizationType | null>(null);
	const { trackingService } = useServices();
	const profileRole = useProfileRole();
	const organizationTypes: readonly OrganizationType[] =
		profileRole === "LEARNER" ? ["COMPANY"] : profileRole === "TEACHER" ? ["INSTITUTE"] : ["INSTITUTE", "COMPANY"];
	const forcedType = organizationTypes.length === 1 ? organizationTypes[0] : null;
	const organizationType = forcedType ?? selectedType;
	const organization = useMemo(
		() => ({
			primaryName: "",
			description: "",
			categories: [],
			assets: [],
		}),
		[]
	);
	useEffect(() => {
		window.scrollTo(0, 0);
	}, [organizationType]);

	return (
		<Container>
			{organizationType ? (
				<EditorWrapper>
					<Title>{I18n.t(`organization.create.${organizationType.toLowerCase()}.title`)}</Title>
					<OrganizationEditor initialOrganization={{ ...organization, type: organizationType }} />
				</EditorWrapper>
			) : (
				<>
					<Title>{I18n.t("organization.create.switch.title")}</Title>
					<Content>
						{organizationTypes.map((orgType) => (
							<CreationCard
								key={orgType}
								whileHover={{ boxShadow: "0 4px 24px 0 rgba(0, 0, 0, 0.05)", translateY: -2 }}
								transition={{ type: "tween" }}
								onClick={() => {
									if (orgType === "COMPANY") {
										trackingService.track("Pro page - selected company page");
									}

									if (orgType === "INSTITUTE") {
										trackingService.track("Pro page - selected institute page");
									}
									setSelectedType(orgType);
								}}
							>
								<TitleContainer>
									<ProfilePicture size={"L"} fallbackType={orgType} shadowed />
									<Title>{I18n.t(`organization.create.${orgType.toLowerCase()}.title`)}</Title>
								</TitleContainer>
								<Primary>{I18n.t(`organization.create.${orgType.toLowerCase()}.description`)}</Primary>
								<NextButton />
							</CreationCard>
						))}
					</Content>
				</>
			)}
			{organizationType && !forcedType ? (
				<TertiaryButton onClick={() => setSelectedType(null)}>{I18n.t("cancel")}</TertiaryButton>
			) : (
				<Link to={Path.Root}>
					<TertiaryButton>{I18n.t("cancel")}</TertiaryButton>
				</Link>
			)}
		</Container>
	);
};

const Container = styled(Page)`
	${stack(40, "center", "center")};
`;

const Content = styled.div`
	@media ${desktop} {
		${row(40, "center", "stretch")};
	}
	@media ${mobile} {
		${stack(20, "flex-start", "stretch")};
	}
`;

const CreationCard = styled(motion.create(RoundedBlock))`
	cursor: pointer;
	flex-grow: 0;
	width: 546px;
	padding: 100px 60px 30px;
	${stack(40)};
	> button {
		align-self: flex-end;
		margin-right: -30px;
	}

	@media ${mobile} {
		width: initial;
		padding: 40px 20px;
		${stack(20)};

		> button {
			margin-right: initial;
		}
	}
`;

const TitleContainer = styled.div`
	${row(14, "flex-start", "center")};
`;

const Title = styled.div`
	${({ theme }) => theme.textStyles.title};
`;

const EditorWrapper = styled(RoundedBlock)`
	padding: 40px;
	width: 100%;
	max-width: 900px;
	${gap(40)};
`;
